import { useState } from 'react'
import { useAtomValue, useSetAtom } from 'jotai'
import { useNavigate } from 'react-router'
import { authenticationState, customerState, authenticationApi } from '@repo/unicity'
import type { TAuthentication } from '@repo/unicity'
import { countryState } from '@/core/store/country.store'
import { fetchFeelGreatSettings } from '@/core/feelgreat/feelgreat.api'
import { feelgreatSettingsAtom } from '@/core/feelgreat/feelgreat.store'
import { selectedFlavorAtom, selectedPurchaseOptionAtom } from '@/core/products/products.store'
import { ORDERCALC_COUNTRIES } from '@/core/http'
import { loginApi, extractToken, checkGeoAllow, checkAcceptLanguage } from './auth.api'
import { getDefaultUserRoute } from './post-login-route.helpers'
import { userStatusAtom, persistUserStatus } from './user-status.store'
import { analytics } from '@/core/analytics/analytics'
import type { UserStatus } from './user-status.store'

const LOGIN_CREDENTIAL_ERROR_KEY = 'login_or_password_incorrect'

const AUTH_STORAGE_KEYS = {
    AUTHENTICATION: 'u25_authentication',
    CUSTOMER: 'u25_customer',
    AUTH_TIMESTAMP: 'u25_auth_timestamp'
} as const

const persistSession = (auth: TAuthentication, countryCode: string, profile?: Record<string, unknown>): void => {
    try {
        const customerData = profile ?? { href: auth.href, market: countryCode }
        localStorage.setItem(AUTH_STORAGE_KEYS.AUTHENTICATION, JSON.stringify(auth))
        localStorage.setItem(AUTH_STORAGE_KEYS.CUSTOMER, JSON.stringify(customerData))
        localStorage.setItem(AUTH_STORAGE_KEYS.AUTH_TIMESTAMP, Date.now().toString())
    } catch {
        // localStorage not available
    }
}

export const useLogin = (options?: { onSuccess?: () => void }) => {
    const [isLoading, setIsLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)

    const country = useAtomValue(countryState)
    const setAuthentication = useSetAtom(authenticationState)
    const setCustomer = useSetAtom(customerState)
    const setUserStatus = useSetAtom(userStatusAtom)
    const setFeelgreatSettings = useSetAtom(feelgreatSettingsAtom)
    const setSelectedFlavor = useSetAtom(selectedFlavorAtom)
    const setSelectedPurchaseOption = useSetAtom(selectedPurchaseOptionAtom)
    const navigate = useNavigate()

    const login = async (username: string, password: string) => {
        setIsLoading(true)
        setError(null)
        analytics.track('loginSubmitted')

        try {
            let response
            try {
                response = await loginApi(username.trim(), password, country.short)
            } catch {
                analytics.track('loginFailed', { reason: 'bad_credentials' })
                setError(LOGIN_CREDENTIAL_ERROR_KEY)
                return
            }
            const token = extractToken(response)

            if (!token) {
                // 2xx with no token — a different failure from a rejected credential, and one
                // that has no user-visible tell beyond the same message.
                analytics.track('loginFailed', { reason: 'no_token' })
                setError(LOGIN_CREDENTIAL_ERROR_KEY)
                return
            }

            const authData: TAuthentication = {
                baId: response.baId,
                customer: { href: response.href },
                expired: false,
                href: response.href,
                log_id: null,
                duration: '',
                token,
                loginTokenSecondsLeft: 0
            }

            setAuthentication(authData)
            persistSession(authData, country.short)
            analytics.identify(response.baId)
            analytics.track('loginSucceeded', { ba_status: (response.userStatus as string | undefined) ?? '' })

            const userStatus = response.userStatus as UserStatus | undefined
            if (userStatus) {
                setUserStatus(userStatus)
                persistUserStatus(userStatus)
            }

            // Step 2: GeoIP check — redirect out if not allowed
            try {
                const geo = await checkGeoAllow(response.baId, country.short)
                if (geo.isAllow === false) {
                    // Signed in, then bounced off-site. Without this the trace just stops.
                    analytics.track('loginGeoBlocked')
                    window.location.replace('https://www.unicity.com')
                    return
                }
            } catch {
                /* geo check failed — proceed */
            }

            // Step 3: Accept-language check (fire-and-forget, non-blocking)
            checkAcceptLanguage(response.baId, country.short).catch(() => {})

            const needsOrderCalc = (ORDERCALC_COUNTRIES as readonly string[]).includes(country.short)

            const [onselfResult] = await Promise.allSettled([
                authenticationApi.onself({
                    countryCode3alpha: country.short,
                    baId: response.baId,
                    token
                }),
                userStatus
                    ? fetchFeelGreatSettings({
                          countryAlpha3: country.short,
                          baStatus: userStatus,
                          ordercalC: needsOrderCalc,
                          baId: needsOrderCalc ? response.baId : undefined
                      }).then(data => {
                          setFeelgreatSettings(data)
                          if (data.defaultValues?.flavor) setSelectedFlavor(data.defaultValues.flavor)
                          if (data.defaultValues?.purchaseOption) setSelectedPurchaseOption(data.defaultValues.purchaseOption)
                      })
                    : Promise.resolve()
            ])

            if (onselfResult.status === 'fulfilled') {
                const profile = onselfResult.value?.data?.ushop?.profile
                if (profile) {
                    setCustomer(profile)
                    persistSession(authData, country.short, profile)
                }
            }

            if (options?.onSuccess) {
                options.onSuccess()
            } else {
                navigate(getDefaultUserRoute(userStatus), { replace: true })
            }

            // Return the fresh auth so callers (e.g. guest enroll → auto-login)
            // can act on it without waiting for the atom/closure to update.
            return authData
        } catch (err: unknown) {
            const axiosErr = err as { response?: { data?: { message?: string; error?: string } }; message?: string }
            const serverMessage = axiosErr.response?.data?.message || axiosErr.response?.data?.error
            analytics.track('loginFailed', { reason: 'post_login', has_api_message: Boolean(serverMessage) })
            setError(serverMessage || 'Login failed. Please try again.')
        } finally {
            setIsLoading(false)
        }
    }

    return { login, isLoading, error }
}
